// ============================================
// ARCHIVER / DÉSARCHIVER — fenêtres de confirmation
// ============================================
// Archiver = passer en « Fermé » (archives.ts). La fenêtre dit ce qui se passe
// et propose « Sans prévenir le client » (décochée par défaut). Désarchiver =
// repasser en « Réouvert », confirmation simple. Les deux servent à la fiche
// (un dossier), à la liste des dossiers et à l'onglet Archives (plusieurs).

import React, { useEffect, useState } from 'react';
import { Archive, ArchiveRestore, Ban, Loader2 } from 'lucide-react';
import { Button, Modal } from '../../shared/ui';
import { libelleSansPrevenir, texteConfirmationArchivage, texteConfirmationDesarchivage } from './archives';
import { LIBELLE_HORS_SAV } from './horsSav';

const TACTILE = 'max-lg:min-h-11';

/** Message d'une erreur levée par l'appelant. */
const messageErreur = (e: unknown, defaut: string) => (e instanceof Error && e.message ? e.message : defaut);

export const FenetreArchivage: React.FC<{
  ouvert: boolean;
  /** Nombre de dossiers archivés (1 : la fiche). */
  nombre?: number;
  onFermer: () => void;
  /** Enregistre ; une erreur levée est affichée et la fenêtre reste ouverte. */
  onConfirmer: (sansPrevenir: boolean) => Promise<void> | void;
}> = ({ ouvert, nombre = 1, onFermer, onConfirmer }) => {
  const [sansPrevenir, setSansPrevenir] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Chaque ouverture repart de la case décochée.
  useEffect(() => { if (ouvert) { setSansPrevenir(false); setErreur(null); } }, [ouvert]);

  const confirmer = async () => {
    if (enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await onConfirmer(sansPrevenir);
      onFermer();
    } catch (e) {
      setErreur(messageErreur(e, 'L’archivage n’a pas pu être enregistré.'));
    } finally {
      setEnCours(false);
    }
  };

  const titre = nombre > 1 ? `Archiver ${nombre} dossiers ?` : 'Archiver ce dossier ?';
  return (
    <Modal isOpen={ouvert} onClose={() => { if (!enCours) onFermer(); }} title={titre} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-encre leading-relaxed">{texteConfirmationArchivage(nombre)}</p>
        <label className={`flex items-center gap-3 rounded-lg border border-trait bg-papier px-3 py-2 text-sm font-semibold text-encre cursor-pointer select-none ${TACTILE}`}>
          <input
            type="checkbox"
            checked={sansPrevenir}
            onChange={e => setSansPrevenir(e.target.checked)}
            disabled={enCours}
            className="w-5 h-5 rounded border-trait text-sapin-700 focus:ring-sapin-600"
          />
          {libelleSansPrevenir(nombre)}
        </label>
        {sansPrevenir && (
          <p className="text-xs text-graphite">Aucun e-mail ni notification ne partira pour ce changement. L’historique du dossier le notera.</p>
        )}
        {erreur && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{erreur}</p>}
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" disabled={enCours} onClick={onFermer} className={`w-full sm:w-auto ${TACTILE}`}>Annuler</Button>
          <Button variant="primary" disabled={enCours} onClick={() => void confirmer()} className={`w-full sm:w-auto ${TACTILE}`}>
            {enCours ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" /> : <Archive className="w-4 h-4 mr-1.5" aria-hidden="true" />}
            Archiver
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const FenetreDesarchivage: React.FC<{
  ouvert: boolean;
  nombre?: number;
  onFermer: () => void;
  onConfirmer: () => Promise<void> | void;
}> = ({ ouvert, nombre = 1, onFermer, onConfirmer }) => {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => { if (ouvert) setErreur(null); }, [ouvert]);

  const confirmer = async () => {
    if (enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await onConfirmer();
      onFermer();
    } catch (e) {
      setErreur(messageErreur(e, 'Le désarchivage n’a pas pu être enregistré.'));
    } finally {
      setEnCours(false);
    }
  };

  const titre = nombre > 1 ? `Désarchiver ${nombre} dossiers ?` : 'Désarchiver ce dossier ?';
  return (
    <Modal isOpen={ouvert} onClose={() => { if (!enCours) onFermer(); }} title={titre} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-encre leading-relaxed">{texteConfirmationDesarchivage(nombre)}</p>
        {erreur && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{erreur}</p>}
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" disabled={enCours} onClick={onFermer} className={`w-full sm:w-auto ${TACTILE}`}>Annuler</Button>
          <Button variant="primary" disabled={enCours} onClick={() => void confirmer()} className={`w-full sm:w-auto ${TACTILE}`}>
            {enCours ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" /> : <ArchiveRestore className="w-4 h-4 mr-1.5" aria-hidden="true" />}
            Désarchiver
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/** Petit badge « Pas une demande SAV » d'un dossier clos avec ce motif. */
export const BadgeHorsSav: React.FC<{ dossier: { horsSav?: boolean | null }; className?: string }> = ({ dossier, className = '' }) => (
  dossier.horsSav ? (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-trait bg-brume px-2 py-0.5 text-[11px] font-semibold text-graphite ${className}`}
      title="Clos comme « Pas une demande SAV », sans notification au client">
      <Ban className="w-3 h-3" aria-hidden="true" />{LIBELLE_HORS_SAV}
    </span>
  ) : null
);
